import React, { useState, useEffect } from 'react';
import { Users, Library as LibraryIcon } from 'lucide-react';
import * as api from '../lib/api';
import { slugify } from '../lib/spaceSlug';
import { useAuth } from '../contexts/AuthContext';
import SpaceRow from './SpaceRow';
import { useLibraryEnabled } from '../hooks/useLibraryEnabled';

const Sidebar = ({ onNavigate, currentPage }) => {
  const { profile } = useAuth();
  const libraryEnabled = useLibraryEnabled();
  const showLibrary = libraryEnabled === true || profile?.is_admin === true;
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.getGroups()
      .then((res) => setGroups(res.data || []))
      .catch((err) => console.error('Error fetching groups:', err));
  }, []);

  return (
    <aside className="hidden lg:block relative z-30 w-full lg:col-span-3 p-4 lg:p-6" data-testid="left-sidebar">
      <div className="sidebar-scroll sticky top-24 lg:max-h-[calc(100vh-7rem)] overflow-y-auto overflow-x-hidden -mx-1 px-1 -mt-1 pt-1 pb-4" data-testid="left-sidebar-scroll">
        <div className="glass-card rounded-arch-xl p-4 mb-4">
          <h2 className="font-heading text-lg font-medium text-primary mb-4 px-2">
            Community Spaces
          </h2>

          {/* All Posts — goes to main feed */}
          <button
            onClick={() => onNavigate('feed')}
            className={`w-full flex items-center gap-3 p-4 rounded-arch-lg mb-2 transition-all ${
              currentPage === 'feed'
                ? 'sidebar-active bg-primary/10'
                : 'hover:bg-primary/5'
            }`}
            data-testid="all-posts-btn"
          >
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" aria-hidden="true" />
            </div>
            <div className="text-left">
              <p className="font-medium text-text-main">All Posts</p>
              <p className="text-sm text-text-muted">Main community feed</p>
            </div>
          </button>

          {/* Spaces: coming-soon ones are visible but not clickable (admins can preview) */}
          <div className="space-y-2">
            {groups.map((group) => (
              <SpaceRow
                key={group.id}
                group={group}
                isActive={currentPage === `space:${group.id}` || currentPage === `space:${slugify(group.name)}`}
                canOpen={profile?.is_admin === true}
                onOpen={(g) => onNavigate(`space:${slugify(g.name)}`)}
              />
            ))}
          </div>

          {showLibrary && (
            <button
              onClick={() => onNavigate('library')}
              className={`w-full flex items-center gap-3 p-4 mt-2 rounded-arch-lg transition-all ${currentPage === 'library' ? 'sidebar-active bg-primary/10' : 'hover:bg-primary/5'}`}
              data-testid="sidebar-library-link"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-lavender/20">
                <LibraryIcon className="w-5 h-5 text-lavender-dark" aria-hidden="true" />
              </div>
              <div className="text-left min-w-0">
                <p className="font-medium text-text-main">Library</p>
                <p className="text-sm text-text-muted truncate">Guides, tools and resources</p>
              </div>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
